import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signInWithRedirect,
    getRedirectResult,
    GoogleAuthProvider,
    signOut,
    onAuthStateChanged,
} from 'firebase/auth';
import type { User } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';
import { AnalyticsEvent, markJustSignedUp, trackEvent } from './analytics';

const googleProvider = new GoogleAuthProvider();

// Set before navigating to Google so the next app load knows it's returning from an OAuth
// redirect and must call getRedirectResult. Without a pending redirect we skip that call —
// it otherwise blocks every cold load ~470ms even though the user is already signed in.
const GOOGLE_REDIRECT_FLAG = 'pendingGoogleRedirect';

export function hasPendingGoogleRedirect(): boolean {
    try {
        return sessionStorage.getItem(GOOGLE_REDIRECT_FLAG) === '1';
    } catch {
        return false;
    }
}

export async function register(email: string, password: string) {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    return result.user;
}

export async function login(email: string, password: string) {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
}

export async function signInWithGoogle(): Promise<void> {
    try {
        sessionStorage.setItem(GOOGLE_REDIRECT_FLAG, '1');
    } catch {
        // ignore — handleGoogleRedirect still completes auth, just without the fast-path skip
    }
    await signInWithRedirect(auth, googleProvider);
}

export async function handleGoogleRedirect(): Promise<User | null> {
    try {
        sessionStorage.removeItem(GOOGLE_REDIRECT_FLAG);
    } catch {
        // ignore
    }
    const result = await getRedirectResult(auth);
    if (!result) return null;

    const user = result.user;

    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
        await setDoc(userRef, {
            email: user.email,
            fullName: user.displayName || '',
            avatarImage: user.photoURL || '',
            createdAt: serverTimestamp()
        });
        // New Google account — funnel step 1. Flag so the first authenticated
        // load can tell whether they landed on an invited workspace.
        markJustSignedUp();
        trackEvent(AnalyticsEvent.SignedUp, { method: 'google' });
    }

    return user;
}

export async function logout() {
    await signOut(auth);
}

export function subscribeToAuth(callback: (user: User | null) => void) {
    return onAuthStateChanged(auth, callback);
}